import {
  FocusEvent,
  FormEvent,
  InputHTMLAttributes,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNumberField } from "react-aria";
import type { NumberFieldProps } from "react-aria-components";
import {
  ButtonContext,
  FieldErrorContext,
  GroupContext,
  InputContext,
  LabelContext,
  Provider,
} from "react-aria-components";
import { useNumberFieldState } from "react-stately";

function convertNullToNaN<T>(value: T | null) {
  return value === null ? NaN : value;
}

function convertNaNToNull<T>(value: T | number) {
  return typeof value === "number" && isNaN(value) ? null : value;
}

export interface CustomAriaNumberFieldProps extends Omit<
  NumberFieldProps,
  "className" | "value" | "defaultValue" | "onChange" | "formatOptions"
> {
  className?: string;
  value?: number | null;
  defaultValue?: number | null;
  onChange?: (value: number | null) => void;
  minDecimals?: number;
  maxDecimals?: number;
}

// TODO : implement step functionality
export function CustomAriaNumberField(props: CustomAriaNumberFieldProps) {
  const locale = "da-DK";

  const { formatOptions, ariaNumberFieldProps } = useMemo(() => {
    const {
      value,
      defaultValue,
      onChange,
      minDecimals,
      maxDecimals = 3,
      ...baseAriaNumberFieldProps
    } = props;

    const formatOptions: Intl.NumberFormatOptions = {
      signDisplay: "negative",
      minimumFractionDigits: minDecimals,
      maximumFractionDigits: maxDecimals,
    };

    const ariaNumberFieldProps: NumberFieldProps = {
      ...baseAriaNumberFieldProps,
      value: convertNullToNaN(value),
      defaultValue: convertNullToNaN(defaultValue),
      onChange: (value) => onChange?.(convertNaNToNull(value)),
      formatOptions,
    };

    return {
      formatOptions,
      ariaNumberFieldProps,
    };
  }, [props]);

  const {
    decimalSeparator,
    parseNumber,
    formatNumber,
    isNumeralChar,
    sanitizeTextValue,
    sanitizeNumberValue,
  } = useMemo(() => {
    const numberFormat = new Intl.NumberFormat(locale, formatOptions);

    const parts = numberFormat.formatToParts(12345.6789);
    const groupSeparator = parts.find((d) => d.type === "group")?.value ?? null;
    const decimalSeparator =
      parts.find((d) => d.type === "decimal")?.value ?? null;

    const numerals = [
      ...new Intl.NumberFormat(locale, { useGrouping: false }).format(
        9876543210,
      ),
    ].reverse();

    const numeralsMap = new Map(numerals.map((d, i) => [d, i]));
    const numeralRegExp = new RegExp(`[${numerals.join("")}]`, "g");
    const nonNumeralRegExp = new RegExp(`[^${numerals.join("")}]`, "g");
    const groupRegExp =
      groupSeparator !== null ? new RegExp(`[${groupSeparator}]`, "g") : null;
    const decimalRegExp =
      decimalSeparator !== null ? new RegExp(`[${decimalSeparator}]`) : null;

    const parseNumber = (string: string) => {
      let parsed = string.trim();

      if (groupRegExp) parsed = parsed.replace(groupRegExp, "");
      if (decimalRegExp) parsed = parsed.replace(decimalRegExp, ".");
      parsed = parsed.replace(numeralRegExp, (d) =>
        String(numeralsMap.get(d) ?? ""),
      );

      return parsed ? +parsed : NaN;
    };

    const formatNumber = (number: number) => {
      if (isNaN(number)) return "";
      return numberFormat.format(number);
    };

    const formatNumberToParts = (number: number) => {
      if (isNaN(number)) return [];
      return numberFormat.formatToParts(number);
    };

    const isNumeralChar = (char: string) => {
      return numeralsMap.has(char);
    };

    const removeNonNumerical = (string: string) => {
      return string.replaceAll(nonNumeralRegExp, "");
    };

    const sanitizeTextValue = (textValue: string) => {
      let newTextValue = textValue.trim();

      const signPart = newTextValue.startsWith("-") ? "-" : "";
      const decimalSeparatorIndex = decimalSeparator
        ? newTextValue.lastIndexOf(decimalSeparator)
        : -1;
      let leadingZeroesPart = "";
      let wholePart = removeNonNumerical(
        decimalSeparatorIndex === -1
          ? newTextValue
          : newTextValue.substring(0, decimalSeparatorIndex),
      );
      const decimalPart = removeNonNumerical(
        decimalSeparatorIndex === -1
          ? ""
          : newTextValue.substring(decimalSeparatorIndex + 1),
      );

      if (wholePart.length > 1) {
        let leadingZeroesEndIndex = -1;
        for (const [i, char] of wholePart.slice(0, -1).split("").entries()) {
          if (char !== "0") break;
          leadingZeroesEndIndex = i + 1;
        }
        if (leadingZeroesEndIndex > 0) {
          leadingZeroesPart = wholePart.substring(0, leadingZeroesEndIndex);
          wholePart = wholePart.substring(leadingZeroesEndIndex);
        }
      }

      // If the integer part is only zeroes, format it as if the first digit is a 1.
      // For example "00000" -> "10000" -> "10.000" -> "00.000"
      if (leadingZeroesPart && wholePart === "0") {
        const tempNumberValue = parseNumber(`1${leadingZeroesPart}`);
        newTextValue = formatNumberToParts(tempNumberValue)
          .map((part) => {
            if (part.type === "integer") return part.value.replace(/1/g, "0");
            return part.value;
          })
          .join("");
      } else {
        newTextValue = leadingZeroesPart;
        newTextValue += formatNumber(parseNumber(wholePart));
      }

      if (!newTextValue.startsWith(signPart)) {
        newTextValue = `${signPart}${newTextValue}`;
      }
      if (decimalSeparatorIndex !== -1) {
        newTextValue += `${decimalSeparator}${decimalPart}`;
      }

      return newTextValue;
    };

    const sanitizeNumberValue = (numberValue: number) => {
      const textValue = formatNumber(numberValue);
      return parseNumber(sanitizeTextValue(textValue));
    };

    return {
      numberFormat,
      groupSeparator,
      decimalSeparator,
      numeralRegExp,
      nonNumeralRegExp,
      parseNumber,
      formatNumber,
      formatNumberToParts,
      isNumeralChar,
      removeNonNumerical,
      sanitizeTextValue,
      sanitizeNumberValue,
    };
  }, [locale, formatOptions]);

  const inputRef = useRef<HTMLInputElement>(null);

  const state = {
    ...useNumberFieldState({
      ...ariaNumberFieldProps,
      locale,
      value: undefined,
      defaultValue: props.value ?? props.defaultValue ?? NaN,
    }),
    validate: () => {
      return true;
    },
  };

  const numberField = useNumberField(ariaNumberFieldProps, state, inputRef);

  const {
    labelProps,
    groupProps,
    incrementButtonProps,
    decrementButtonProps,
    isInvalid,
    validationDetails,
    validationErrors,
  } = numberField;

  useEffect(() => {
    const input = inputRef.current;

    if (props.value === undefined) return;

    if (props.value === null) {
      if (isNaN(state.numberValue)) return;
      if (input) input.value = "";
      state.setInputValue("");
      state.setNumberValue(NaN);
      return;
    }

    if (props.value === state.numberValue) return;

    const sanitizedNumberValue = sanitizeNumberValue(props.value);

    if (sanitizedNumberValue !== state.numberValue) {
      const newTextValue = formatNumber(sanitizedNumberValue);
      if (input) input.value = newTextValue;
      state.setInputValue(newTextValue);
      state.setNumberValue(sanitizedNumberValue);
    }
  }, [props.value]);

  const [prevInputState] = useState<{
    value: string;
    selectionDirection: "forward" | "backward" | "none" | null;
    selectionStart: number | null;
    selectionEnd: number | null;
  }>({
    value: "",
    selectionDirection: null,
    selectionStart: null,
    selectionEnd: null,
  });

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const input = event.target as HTMLInputElement;
    prevInputState.value = input.value;
    prevInputState.selectionDirection = input.selectionDirection;
    prevInputState.selectionStart = input.selectionStart;
    prevInputState.selectionEnd = input.selectionEnd;

    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case "z":
        case "y":
          event.preventDefault();
          return;
      }
    } else {
      const { minValue, maxValue, maxDecimals } = props;

      switch (event.key) {
        case "+":
          if (maxValue !== undefined && maxValue <= 0) event.preventDefault();
          return;

        case "-":
          if (minValue !== undefined && minValue >= 0) event.preventDefault();
          return;

        case decimalSeparator:
          if (maxDecimals !== undefined && maxDecimals <= 0)
            event.preventDefault();
          return;
      }
    }
  };

  const onInput = (event: FormEvent<HTMLInputElement>) => {
    const input = event.target as HTMLInputElement;

    const inputState = {
      value: input.value,
      selectionDirection: input.selectionDirection,
      selectionStart: input.selectionStart,
      selectionEnd: input.selectionEnd,
    };

    const cursorPos = inputState.selectionStart;

    const newTextValue = sanitizeTextValue(inputState.value);
    let newNumberValue = parseNumber(newTextValue);

    input.value = newTextValue;
    state.setInputValue(newTextValue);

    if (!isNaN(newNumberValue)) {
      const {
        minValue = null,
        maxValue = null,
        // step = null,
      } = props;

      if (minValue !== null && newNumberValue < minValue) {
        newNumberValue = minValue;
      }

      if (maxValue !== null && newNumberValue > maxValue) {
        newNumberValue = maxValue;
      }

      // if (step !== null) {
      //   console.log(newNumberValue % step);
      //   const remainder = (newNumberValue) % step;
      // }

      newNumberValue = sanitizeNumberValue(newNumberValue);
    }

    state.setNumberValue(newNumberValue);

    if (newTextValue === inputState.value) return;

    if (newTextValue === prevInputState.value) {
      input.selectionDirection = "none";
      input.selectionStart = prevInputState.selectionEnd;
      input.selectionEnd = prevInputState.selectionEnd;
      return;
    }

    if (cursorPos !== null) {
      const isCursorChar = (string: string, i: number) => {
        const char = string[i];
        if (i === 0 && char === "-") return true;
        if (
          decimalSeparator &&
          i === string.lastIndexOf(decimalSeparator) &&
          char === decimalSeparator
        )
          return true;
        return isNumeralChar(char);
      };

      const countCursorChars = () => {
        let count = 0;
        for (const i of inputState.value.split("").keys()) {
          if (i === cursorPos) break;
          if (!isCursorChar(inputState.value, i)) continue;
          count++;
        }
        return count;
      };

      const cursorCharCount = countCursorChars();

      const getNewCursorPos = () => {
        let newCursorPos = 0;
        let newCursorCharCount = 0;
        for (const i of newTextValue.split("").keys()) {
          if (newCursorCharCount === cursorCharCount) break;
          newCursorPos++;
          if (isCursorChar(newTextValue, i)) newCursorCharCount++;
        }
        return newCursorPos;
      };

      const newCursorPos = getNewCursorPos();

      input.selectionDirection = "none";
      input.selectionStart = newCursorPos;
      input.selectionEnd = newCursorPos;
    }
  };

  const onBlur = (event: FocusEvent<HTMLInputElement>) => {
    const input = event.target as HTMLInputElement;

    let numberValue = parseNumber(input.value);
    let newTextValue = "";

    if (!isNaN(numberValue)) {
      const { minValue = null, maxValue = null } = props;

      if (minValue !== null && numberValue < minValue) {
        numberValue = minValue;
      }

      if (maxValue !== null && numberValue > maxValue) {
        numberValue = maxValue;
      }

      newTextValue = formatNumber(numberValue);
      numberValue = parseNumber(newTextValue);
    }

    input.value = newTextValue;
    state.setInputValue(newTextValue);
    state.setNumberValue(numberValue);
  };

  const inputProps: InputHTMLAttributes<HTMLInputElement> = {
    ...numberField.inputProps,
    value: undefined,
    defaultValue: formatNumber(state.numberValue),
    onKeyDown: onKeyDown,
    onInput: onInput,
    onBlur: onBlur,
  };

  const children =
    typeof props.children === "function"
      ? props.children({
          state,
          isRequired: props.isRequired ?? false,
          isDisabled: props.isDisabled ?? false,
          isReadOnly: props.isReadOnly ?? false,
          isInvalid,
          defaultChildren: null,
        })
      : props.children;

  // Provide values for the child components via context.
  return (
    <Provider
      values={[
        [GroupContext, { ...groupProps }],
        [InputContext, { ...inputProps, ref: inputRef }],
        [LabelContext, labelProps],
        [FieldErrorContext, { isInvalid, validationDetails, validationErrors }],
        [
          ButtonContext,
          {
            slots: {
              increment: incrementButtonProps,
              decrement: decrementButtonProps,
            },
          },
        ],
      ]}
    >
      <div className={props.className}>{children}</div>
    </Provider>
  );
}
